import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  savingsVaultCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { savingsEndpoints } from "@/features/savings/api/endpoints";
import type {
  SavingsVaultDto,
  UpdateSavingsVaultRequestDto,
} from "@/features/savings/api/dto";

/**
 * Renomme un coffre ou modifie son objectif (`PUT /SavingsVaults/{id}`) —
 * `goalAmountMinor` null = sans objectif. Titulaire uniquement, coffre
 * OUVERT (422 si clos). Idempotency-Key OBLIGATOIRE (guide §4.7, clé
 * persistée par empreinte du corps).
 */
export const useUpdateSavingsVault = (vaultId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["savings", "savingsVaults", "update", vaultId],
    mutationFn: (body: UpdateSavingsVaultRequestDto) =>
      mutateIdempotent<SavingsVaultDto>({
        scopeId: `savings.savingsVaults.update.${vaultId}`,
        url: savingsEndpoints.savingsVaults.update(vaultId),
        body,
        method: "PUT",
      }),
    onSuccess: () =>
      invalidateAfterMutation(queryClient, savingsVaultCacheKeys(vaultId)),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useUpdateSavingsVault failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "savings" }
      );
    },
  });
};
